/**
 * Site assignments — who is at which site, and when.
 *
 * This is the screen that makes clock-in work. A worker can only clock in inside the
 * geofence of a site they are assigned to *that day*, so an unassigned worker is not a
 * tidiness problem, it is someone who cannot start work.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Modal } from '@/components/Modal';
import { canBeAssigned } from '@/features/access/signIn';
import { MobileList } from '@/components/MobileList';
import { Badge, Card, ErrorNotice, Field, PageHeader, Spinner } from '@/components/ui';
import { useConfirm } from '@/components/ConfirmDialog';
import { useDir, useI18n } from '@/i18n';
import { api, ApiError, type User } from '@/lib/api';
import { dateShort, isoDate } from '@/lib/format';
import { Combobox } from '@/components/Combobox';
import { jobOptions, PeopleMultiSelect, siteOptions } from '@/features/access/people';

function addDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return isoDate(d);
}

export function AssignmentsPage() {
  const { t, plural } = useI18n();
  const { forward } = useDir();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const today = isoDate(new Date());

  const [on, setOn] = useState(today);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    userIds: [] as string[],
    siteId: '',
    jobId: '',
    startsOn: today,
    endsOn: '',
    openEnded: true,
    notes: '',
  });

  const assignments = useQuery({
    queryKey: ['assignments', on],
    queryFn: () => api.assignments({ from: on, to: on }),
  });
  const unassigned = useQuery({
    queryKey: ['unassigned', on],
    queryFn: () => api.unassignedWorkers(on),
  });
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.users() });
  const sites = useQuery({ queryKey: ['sites'], queryFn: api.sites });
  const jobs = useQuery({ queryKey: ['jobs'], queryFn: () => api.jobs() });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['assignments'] });
    await queryClient.invalidateQueries({ queryKey: ['unassigned'] });
  };

  const create = useMutation({
    mutationFn: () =>
      api.createAssignment({
        userIds: form.userIds,
        siteId: form.siteId,
        ...(form.jobId ? { jobId: form.jobId } : {}),
        startsOn: form.startsOn,
        ...(form.openEnded || !form.endsOn ? {} : { endsOn: form.endsOn }),
        ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
      }),
    onSuccess: async () => {
      setCreating(false);
      setForm({ ...form, userIds: [], notes: '' });
      await refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.removeAssignment(id),
    onSuccess: refresh,
  });

  /** Grouped by site — the office thinks in crews, not in individual rows. */
  const bySite = useMemo(() => {
    const map = new Map<string, typeof rows>();
    const rows = assignments.data?.assignments ?? [];
    for (const a of rows) {
      const list = map.get(a.siteName);
      if (list) list.push(a);
      else map.set(a.siteName, [a]);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0], 'it'));
  }, [assignments.data]);

  const engineers = (users.data?.users ?? []).filter(
    (u: User) => canBeAssigned(u),
  );

  const canCreate = form.userIds.length > 0 && form.siteId && form.startsOn && !create.isPending;


  return (
    <>
      <PageHeader
        title={t('assignments.title')}
        subtitle={t('assignments.subtitle')}
        actions={
          <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
            {t('assignments.create')}
          </button>
        }
      />

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        size="lg"
        title={t('assignments.createTitle')}
        footer={
          <>
            <button
              type="button"
              className="btn-primary"
              disabled={!canCreate}
              onClick={() => create.mutate()}
            >
              {create.isPending
                ? t('assignments.assigning')
                : plural(form.userIds.length, 'assignments.assignOne', 'assignments.assignMany', {
                    count: form.userIds.length,
                  })}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setCreating(false)}>
              {t('common.cancel')}
            </button>

            {create.error ? (
              <p className="text-sm text-danger ms-auto">
                {create.error instanceof ApiError
                  ? create.error.message
                  : t('assignments.createError')}
              </p>
            ) : null}
          </>
        }
      >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <Field label={t('common.site')}>
              <Combobox
                value={form.siteId}
                onChange={(siteId) => setForm({ ...form, siteId })}
                options={siteOptions(sites.data?.sites ?? [])}
              />
            </Field>

            <Field label={t('assignments.jobOptional')} hint={t('assignments.jobOptionalHint')}>
              <Combobox
                value={form.jobId}
                onChange={(jobId) => setForm({ ...form, jobId })}
                options={[{ value: '', label: t('assignments.noJob') }, ...jobOptions(jobs.data?.jobs ?? [])]}
              />
            </Field>

            <Field label={t('common.from')}>
              <input
                type="date"
                className="input"
                value={form.startsOn}
                onChange={(e) => setForm({ ...form, startsOn: e.target.value })}
              />
            </Field>
          </div>

          <div className="flex items-end gap-4 mb-4">
            <label className="flex items-center gap-2 text-sm h-9">
              <input
                type="checkbox"
                checked={form.openEnded}
                onChange={(e) => setForm({ ...form, openEnded: e.target.checked })}
              />
              {t('assignments.openEnded')}
            </label>

            {!form.openEnded ? (
              <Field label={t('common.to')}>
                <input
                  type="date"
                  className="input w-44"
                  value={form.endsOn}
                  min={form.startsOn}
                  onChange={(e) => setForm({ ...form, endsOn: e.target.value })}
                />
              </Field>
            ) : null}

            <div className="flex gap-2 ms-auto">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setForm({ ...form, startsOn: today, endsOn: today, openEnded: false })}
              >
                {t('common.onlyToday')}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setForm({ ...form, startsOn: today, endsOn: addDays(6), openEnded: false })}
              >
                {t('common.thisWeek')}
              </button>
            </div>
          </div>

          <Field label={t('assignments.selectedCount', { count: form.userIds.length })} group>
            {/* `wrap`, not `scroll`: thirty names to choose from is a paragraph, and
                a horizontal scroll containing thirty names hides most of them. */}
            <PeopleMultiSelect
              users={engineers}
              selected={form.userIds}
              onChange={(userIds) => setForm({ ...form, userIds })}
            />
          </Field>

          <div className="mt-4">
            <Field label={t('assignments.notesOptional')}>
              <input
                className="input"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder={t('assignments.notesPlaceholder')}
              />
            </Field>
          </div>

      </Modal>

      <Card className="mb-4">
        {/* Wraps, and the date picker gives up its fixed width when it has to: a
            176px input plus two buttons is 19px wider than a phone. */}
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t('common.day')}>
            <input
              type="date"
              className="input w-full sm:w-44"
              value={on}
              onChange={(e) => setOn(e.target.value)}
            />
          </Field>
          <button type="button" className="btn-secondary" onClick={() => setOn(today)}>
            {t('common.today')}
          </button>
          <button type="button" className="btn-secondary" onClick={() => setOn(addDays(1))}>
            {t('common.tomorrow')}
          </button>
        </div>
      </Card>

      {/* The gap that matters: an unassigned worker cannot clock in. */}
      {(unassigned.data?.workers.length ?? 0) > 0 ? (
        <Card className="mb-4 bg-warning-soft border-[var(--color-warning)]">
          <p className="text-sm font-semibold mb-1">
            {plural(unassigned.data?.workers.length ?? 0, 'assignments.unassignedOne', 'assignments.unassignedMany', {
              count: unassigned.data?.workers.length ?? 0,
              date: dateShort(on),
            })}
          </p>
          <p className="text-sm text-muted mb-2">
            {t('assignments.unassignedHint')}
          </p>
          <div className="flex flex-wrap gap-2">
            {(unassigned.data?.workers ?? []).map((w) => (
              <Badge key={w.id} tone="warning">
                {w.name}
              </Badge>
            ))}
          </div>
        </Card>
      ) : null}

      {assignments.isLoading ? (
        <Spinner />
      ) : assignments.error ? (
        <ErrorNotice error={assignments.error} />
      ) : bySite.length === 0 ? (
        <Card>
          <p className="text-sm text-muted">{t('assignments.noneForDay', { date: dateShort(on) })}</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {bySite.map(([siteName, rows]) => (
            <Card key={siteName} padded={false}>
              <div className="px-4 py-3 border-b border-border flex items-center gap-3">
                <h2 className="font-bold">{siteName}</h2>
                <Badge tone="primary">{rows.length}</Badge>
                <span className="text-xs text-muted ms-auto tabular">
                  area {rows[0]?.geofenceRadiusM} m
                </span>
              </div>

              <div className="lg:hidden p-1">
                <MobileList
                  rows={rows}
                  keyOf={(a) => a.id}
                  title={(a) => a.userName}
                  subtitle={(a) => a.jobReference ?? t('assignments.noJobShort')}
                  badges={(a) => (a.endsOn ? null : <Badge tone="primary">{t('assignments.project')}</Badge>)}
                  fields={[
                    {
                      label: t('assignments.startsOn'),
                      value: (a) =>
                        `${dateShort(a.startsOn)} ${forward} ${
                          a.endsOn ? dateShort(a.endsOn) : t('assignments.project')
                        }`,
                    },
                    { label: t('common.notes'), value: (a) => a.notes },
                  ]}
                  action={(a) => (
                    <button
                      type="button"
                      className="btn-danger"
                      onClick={async () => {
                        if (
                          await confirm({
                            message: t('assignments.removeConfirm', { name: a.userName, site: a.siteName }),
                            confirmLabel: t('common.remove'),
                            danger: true,
                          })
                        ) {
                          remove.mutate(a.id);
                        }
                      }}
                    >
                      {t('common.remove')}
                    </button>
                  )}
                />
              </div>

              <div className="table-scroll max-lg:hidden">
                <table>
                  <tbody>
                    {rows.map((a) => (
                      <tr key={a.id} className="hover:bg-surface-alt">
                        <td className="td font-semibold">{a.userName}</td>
                        <td className="td text-muted text-sm">
                          {a.jobReference ?? <span className="text-subtle">{t('assignments.noJobShort')}</span>}
                        </td>
                        <td className="td tabular text-sm text-muted whitespace-nowrap">
                          {dateShort(a.startsOn)} {forward}{' '}
                          {a.endsOn ? (
                            dateShort(a.endsOn)
                          ) : (
                            <Badge tone="primary">{t('assignments.project')}</Badge>
                          )}
                        </td>
                        <td className="td text-sm text-muted">{a.notes}</td>
                        <td className="td text-end">
                          <button
                            type="button"
                            className="td-action-danger"
                            onClick={async () => {
                              if (
                                await confirm({
                                  message: t('assignments.removeConfirm', { name: a.userName, site: a.siteName }),
                                  confirmLabel: t('common.remove'),
                                  danger: true,
                                })
                              ) {
                                remove.mutate(a.id);
                              }
                            }}
                          >
                            {t('common.remove')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
